import { Injectable, signal } from '@angular/core';

/** The fingerprinted entry bundle, e.g. main-CQXCZ3PJ.js. A deploy changes its name. */
const mainBundle = /main-[A-Z0-9]{8}\.js/;

/**
 * Notices when a new version has been deployed while the app is open.
 *
 * It compares the entry bundle this page loaded against the one in the server's current index.html.
 * A tab can stay open for days, so the check runs whenever the app comes back to the foreground
 * as well as on a timer. Reloading is left to the user, so nothing they are typing is lost.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
    private static readonly checkInterval = 10 * 60 * 1000;

    private readonly loadedBundle = document.querySelector('script[src*="main-"]')?.getAttribute('src')?.match(mainBundle)?.[0];
    private started = false;

    readonly updateAvailable = signal(false);

    start() {
        // ng serve serves an unhashed main.js, so there is nothing to compare.
        if (!this.loadedBundle || this.started) return;
        this.started = true;

        setInterval(() => this.check(), AppUpdateService.checkInterval);
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') this.check();
        });
    }

    async check() {
        if (this.updateAvailable()) return;
        try {
            // fetch rather than HttpClient: no bearer token or interceptors needed for index.html.
            const response = await fetch(document.baseURI, { cache: 'no-store' });
            if (!response.ok) return;
            const deployedBundle = (await response.text()).match(mainBundle)?.[0];
            if (deployedBundle && deployedBundle !== this.loadedBundle) this.updateAvailable.set(true);
        } catch {
            // Offline or mid-deploy; the next check will catch it.
        }
    }

    /** Detail screens' beforeunload handler still warns if anything is unsaved. */
    reload() {
        location.reload();
    }
}
